import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpRight, Check, X, RotateCcw, MessageCircle, ChevronRight, ChevronDown, Ban, History, Tag, ThumbsUp, Sparkles, ChevronUp } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import CounterOfferModal from '../components/CounterOfferModal';
import { formatPrice } from '../utils/formatters';
import { OFFER_STATUS_CONFIG, OFFER_CLOSED_STATUSES } from '../constants/marketplace';

const STATUS_CONFIG = OFFER_STATUS_CONFIG;
const CLOSED_STATUSES = OFFER_CLOSED_STATUSES;

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const ITEMS_PER_PAGE = 10;

const OfferCard = ({ offer, type, onAction }) => {
  const vehicle = offer.vehicle || {};
  const latestOffer = offer.latest_offer || offer;
  const statusConf = STATUS_CONFIG[latestOffer.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConf.icon;
  const counterParty = type === 'received' ? offer.buyer : offer.seller;
  const isPending = latestOffer.status === 'pending';
  const isClosed = CLOSED_STATUSES.includes(latestOffer.status);
  const isAccepted = latestOffer.status === 'accepted';
  const [showHistory, setShowHistory] = useState(false);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  // Determine if current user can act on this offer
  const isReceivedPending = type === 'received' && isPending;
  const isSentPending = type === 'sent' && isPending;

  const canActOnReceived = isReceivedPending && (latestOffer.round_number % 2 === 1);
  const canActOnSent = isSentPending && (latestOffer.round_number % 2 === 0);

  const hasHistory = latestOffer.round_number > 1 || latestOffer.parent_offer_id;

  const toggleHistory = async () => {
    if (showHistory) {
      setShowHistory(false);
      return;
    }
    if (!thread) {
      setThreadLoading(true);
      try {
        const res = await api.get(`/marketplace/offers/${offer.id}/thread`);
        setThread(res.data.thread || []);
      } catch (err) {
        console.error('Error loading thread:', err);
      } finally {
        setThreadLoading(false);
      }
    }
    setShowHistory(true);
  };

  return (
    <div className={`bg-dark-card rounded-xl border border-dark-border overflow-hidden transition-all ${
      isClosed ? 'opacity-50 grayscale' : 'hover:border-dark-hover'
    }`}>
      <div className="p-4">
        {/* Positive message when accepted */}
        {isAccepted && (
          <div className="mb-3 rounded-lg p-3 bg-carmin-50 border border-carmin-dark/30">
            <div className="flex items-start gap-2.5">
              {type === 'received' ? (
                <ThumbsUp className="h-5 w-5 text-carmin flex-shrink-0 mt-0.5" />
              ) : (
                <Sparkles className="h-5 w-5 text-carmin flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className="text-sm font-semibold text-carmin-light">
                  {type === 'received' ? 'Accord conclu !' : 'Offre acceptée !'}
                </p>
                <p className="text-xs text-carmin mt-0.5">
                  {type === 'received'
                    ? 'Bravo ! Contactez l\'acheteur pour finaliser la transaction et organiser la livraison.'
                    : 'Excellente nouvelle ! Prenez contact avec le vendeur pour finaliser votre achat.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Top row: vehicle + status */}
        <div className="flex items-start gap-3 mb-3">
          {vehicle.images?.[0] ? (
            <img src={vehicle.images[0]} alt="" className="w-20 h-14 rounded-lg object-cover flex-shrink-0" />
          ) : (
            <div className="w-20 h-14 rounded-lg bg-dark-hover flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <Link to={`/marketplace/${offer.listing_id}`} className="font-semibold text-primary hover:text-carmin transition-colors truncate block">
              {vehicle.make} {vehicle.model}
            </Link>
            <p className="text-xs text-muted mt-0.5">
              {vehicle.year} {vehicle.mileage ? `• ${new Intl.NumberFormat('fr-BE').format(vehicle.mileage)} km` : ''}
            </p>
            <p className="text-xs text-muted mt-0.5">
              {type === 'received' ? 'De' : 'À'} : {counterParty?.display_name || 'Dealer'}
            </p>
          </div>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusConf.color} flex-shrink-0`}>
            <StatusIcon className="h-3 w-3" />
            {statusConf.label}
          </span>
        </div>

        {/* Amount */}
        <div className="flex items-center justify-between bg-dark-hover rounded-lg px-3 py-2 mb-3">
          <div>
            <p className="text-xs text-muted">
              {latestOffer.parent_offer_id ? `Contre-offre (round ${latestOffer.round_number}/3)` : 'Offre initiale'}
            </p>
            <p className="text-xl font-bold text-primary">{formatPrice(latestOffer.amount)}</p>
          </div>
          {offer.listing?.pro_price && (
            <div className="text-right">
              <p className="text-xs text-muted">Prix demandé</p>
              <p className="text-sm font-medium text-secondary">{formatPrice(offer.listing.pro_price)}</p>
            </div>
          )}
        </div>

        {/* Message */}
        {latestOffer.message && (
          <div className="flex items-start gap-2 text-sm text-secondary mb-3 bg-blue-500/10 rounded-lg p-2.5">
            <MessageCircle className="h-3.5 w-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs">{latestOffer.message}</p>
          </div>
        )}

        {/* Negotiation history */}
        {hasHistory && (
          <div className="mb-3">
            <button
              onClick={toggleHistory}
              className="inline-flex items-center gap-1.5 text-xs text-muted hover:text-carmin transition-colors"
            >
              <History className="h-3.5 w-3.5" />
              Historique de la négociation
              {showHistory ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>

            {showHistory && (
              <div className="mt-2 space-y-0 border-l-2 border-dark-border ml-1.5 pl-3">
                {threadLoading ? (
                  <p className="text-xs text-muted py-2">Chargement...</p>
                ) : thread?.map((step, i) => {
                  const stepStatus = STATUS_CONFIG[step.status] || STATUS_CONFIG.pending;
                  const StepIcon = stepStatus.icon;
                  const isBuyer = step.round_number % 2 === 1;
                  return (
                    <div key={step.id} className="relative py-2">
                      {/* Timeline dot */}
                      <div className={`absolute -left-[19px] top-3 w-2.5 h-2.5 rounded-full border-2 border-dark-card ${
                        i === thread.length - 1 ? 'bg-carmin' : 'bg-gray-600'
                      }`} />
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-label">
                            {step.round_number === 1 ? 'Offre initiale' : `Contre-offre #${step.round_number - 1}`}
                            <span className="text-muted font-normal ml-1">
                              — {isBuyer ? (offer.buyer?.display_name || 'Acheteur') : (offer.seller?.display_name || 'Vendeur')}
                            </span>
                          </p>
                          <p className="text-sm font-bold text-primary">{formatPrice(step.amount)}</p>
                          {step.message && (
                            <p className="text-xs text-muted mt-0.5 italic">« {step.message} »</p>
                          )}
                          <p className="text-[10px] text-muted mt-0.5">{formatDate(step.created_at)}</p>
                        </div>
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${stepStatus.color}`}>
                          <StepIcon className="h-2.5 w-2.5" />
                          {stepStatus.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Expiration for pending */}
        {isPending && latestOffer.expires_at && (
          <p className="text-xs text-muted mb-3">
            Expire le {formatDate(latestOffer.expires_at)}
          </p>
        )}

        {/* Contact info when accepted */}
        {isAccepted && counterParty?.phone && (
          <div className="bg-carmin-50 border border-carmin-dark/30 rounded-lg p-3 mb-3">
            <p className="text-xs font-medium text-carmin-light mb-1">Coordonnées</p>
            <a href={`tel:${counterParty.phone}`} className="text-sm text-carmin font-semibold hover:underline">
              {counterParty.phone}
            </a>
          </div>
        )}

        {/* Actions */}
        {(canActOnReceived || canActOnSent) && (
          <div className="flex gap-2">
            <button
              onClick={() => onAction('accept', latestOffer.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-carmin text-white rounded-lg text-sm font-medium hover:bg-carmin-light transition-colors"
            >
              <Check className="h-4 w-4" />
              Accepter
            </button>
            <button
              onClick={() => onAction('counter', latestOffer)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-carmin text-white rounded-lg text-sm font-medium hover:bg-carmin-light transition-colors"
              disabled={latestOffer.round_number >= 3}
            >
              <RotateCcw className="h-4 w-4" />
              Contre-offre
            </button>
            <button
              onClick={() => onAction('reject', latestOffer.id)}
              className="flex items-center justify-center gap-1.5 px-4 py-2.5 border border-red-500/30 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/10 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Cancel for sent pending offers */}
        {isSentPending && (latestOffer.round_number % 2 === 1) && (
          <button
            onClick={() => onAction('cancel', latestOffer.id)}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 border border-dark-border text-secondary rounded-lg text-sm font-medium hover:bg-dark-hover transition-colors"
          >
            <Ban className="h-4 w-4" />
            Annuler mon offre
          </button>
        )}
      </div>
    </div>
  );
};

const MyOffers = () => {
  const [tab, setTab] = useState('received');
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [sentOffers, setSentOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [counterModal, setCounterModal] = useState(null);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const loadOffers = async () => {
    setLoading(true);
    try {
      const [receivedRes, sentRes] = await Promise.all([
        api.get('/marketplace/offers/received'),
        api.get('/marketplace/offers/sent'),
      ]);
      setReceivedOffers(receivedRes.data.offers);
      setSentOffers(sentRes.data.offers);
    } catch (err) {
      console.error('Error loading offers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOffers(); }, []);

  // Reset visible count when switching tabs
  useEffect(() => { setVisibleCount(ITEMS_PER_PAGE); }, [tab]);

  const handleAction = async (action, payload) => {
    if (action === 'counter') {
      setCounterModal(payload);
      return;
    }

    setActionLoading(true);
    try {
      if (action === 'accept') {
        await api.post(`/marketplace/offers/${payload}/accept`);
      } else if (action === 'reject') {
        await api.post(`/marketplace/offers/${payload}/reject`);
      } else if (action === 'cancel') {
        await api.post(`/marketplace/offers/${payload}/cancel`);
      }
      await loadOffers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const allOffers = tab === 'received' ? receivedOffers : sentOffers;
  const visibleOffers = allOffers.slice(0, visibleCount);
  const hasMore = visibleCount < allOffers.length;

  const pendingReceived = receivedOffers.filter(o => {
    const latest = o.latest_offer || o;
    return latest.status === 'pending';
  }).length;
  const pendingSent = sentOffers.filter(o => {
    const latest = o.latest_offer || o;
    return latest.status === 'pending';
  }).length;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <Tag className="h-7 w-7 text-carmin" />
            Mes offres
          </h1>
          <p className="text-sm text-muted mt-1">Gérez vos négociations</p>
        </div>
        <Link
          to="/marketplace"
          className="inline-flex items-center text-sm text-carmin hover:text-carmin-light"
        >
          Marketplace <ChevronRight className="h-4 w-4 ml-0.5" />
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-hover p-1 rounded-xl mb-6 max-w-md">
        <button
          onClick={() => setTab('received')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
            tab === 'received'
              ? 'bg-theme-inverted text-theme-inverted'
              : 'text-secondary hover:text-primary'
          }`}
        >
          <ArrowDownLeft className="h-4 w-4" />
          Reçues
          {pendingReceived > 0 && (
            <span className="bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {pendingReceived}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('sent')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
            tab === 'sent'
              ? 'bg-theme-inverted text-theme-inverted'
              : 'text-secondary hover:text-primary'
          }`}
        >
          <ArrowUpRight className="h-4 w-4" />
          Envoyées
          {pendingSent > 0 && (
            <span className="bg-carmin text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {pendingSent}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <div className="py-12"><LoadingSpinner size="lg" /></div>
      ) : allOffers.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-dark-hover rounded-full flex items-center justify-center mx-auto mb-4">
            {tab === 'received' ? <ArrowDownLeft className="h-8 w-8 text-muted" /> : <ArrowUpRight className="h-8 w-8 text-muted" />}
          </div>
          <p className="text-muted font-medium">
            {tab === 'received' ? 'Aucune offre reçue' : 'Aucune offre envoyée'}
          </p>
          <p className="text-sm text-muted mt-1">
            {tab === 'received'
              ? 'Les offres sur vos annonces apparaîtront ici'
              : 'Parcourez la marketplace pour faire des offres'
            }
          </p>
          {tab === 'sent' && (
            <Link to="/marketplace" className="inline-flex items-center gap-1 mt-4 text-carmin hover:text-carmin-light text-sm font-medium">
              Voir la marketplace <ChevronRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleOffers.map(offer => (
              <OfferCard
                key={offer.id}
                offer={offer}
                type={tab}
                onAction={handleAction}
              />
            ))}
          </div>

          {/* Voir plus / Voir moins */}
          <div className="flex justify-center mt-6 gap-3">
            {hasMore && (
              <button
                onClick={() => setVisibleCount(prev => prev + ITEMS_PER_PAGE)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-dark-hover hover:bg-dark-border text-label rounded-lg text-sm font-medium transition-colors"
              >
                <ChevronDown className="h-4 w-4" />
                Voir plus ({allOffers.length - visibleCount} restantes)
              </button>
            )}
            {visibleCount > ITEMS_PER_PAGE && (
              <button
                onClick={() => setVisibleCount(ITEMS_PER_PAGE)}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-muted hover:text-label rounded-lg text-sm font-medium transition-colors"
              >
                <ChevronUp className="h-4 w-4" />
                Réduire
              </button>
            )}
          </div>
        </>
      )}

      {/* Counter-offer modal */}
      {counterModal && (
        <CounterOfferModal
          offer={counterModal}
          onClose={() => setCounterModal(null)}
          onCounterSent={loadOffers}
        />
      )}

      {/* Loading overlay for actions */}
      {actionLoading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-40">
          <div className="bg-dark-card rounded-xl p-6">
            <LoadingSpinner size="md" />
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOffers;
